/**
 * Admin → Deliveries Calendar (Phase B)
 *
 * Shows materialized upcoming deliveries grouped by day. Admin can mark a
 * delivery as delivered (which advances the parent subscription's next
 * delivery date by one frequency cycle).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Truck, CheckCircle2, Calendar as CalendarIcon, RefreshCw, User } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Business-model calendar palette. `display_status` is computed server-side
// and includes `needs_followup` for stale reminders.
const STATUS_BADGES = {
  scheduled:      { label: '🔵 Scheduled',      cls: 'bg-slate-100 text-slate-800' },
  reminder_sent:  { label: '🟡 Reminder sent',  cls: 'bg-amber-100 text-amber-900' },
  needs_followup: { label: '🔴 Needs follow-up', cls: 'bg-rose-100 text-rose-900' },
  confirmed:      { label: '🟢 Confirmed',      cls: 'bg-emerald-100 text-emerald-900' },
  delivered:      { label: '⚫ Delivered',      cls: 'bg-neutral-800 text-white' },
  cancelled:      { label: '⚠️ Cancelled',      cls: 'bg-neutral-200 text-neutral-700' },
};

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoPlus(days) { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }

async function adminFetch(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

export default function AdminDeliveriesCalendar() {
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoPlus(30));
  const [statusFilter, setStatusFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ from, to });
      if (statusFilter) qs.set('status', statusFilter);
      const d = await adminFetch(`/api/admin/deliveries?${qs.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [from, to, statusFilter]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onMarkDelivered = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await adminFetch(`/api/admin/deliveries/${id}/mark-delivered`, {
        method: 'POST',
        body: JSON.stringify({ note: '' }),
      });
      toast.success('Marked as delivered');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const days = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_day || {}).map(([day, deliveries]) => ({ day, deliveries }));
  }, [data]);

  return (
    <div className="space-y-5" data-testid="admin-deliveries-calendar-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Operations · Deliveries</p>
          <h1 className="font-display text-3xl text-[#05223D]">Deliveries Calendar</h1>
          <p className="text-sm text-[#465B70] mt-1">Upcoming customer deliveries, by day. Mark each one delivered once it's been dropped off.</p>
        </div>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#465B70]">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl" data-testid="deliveries-from" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#465B70]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl" data-testid="deliveries-to" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#465B70]">Status</Label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-xl border border-[#0A4D8C26] bg-white px-3 text-sm" data-testid="deliveries-status">
              <option value="">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Button onClick={load} disabled={loading} className="h-10 rounded-xl bg-[#0A4D8C] hover:bg-[#053B70]" data-testid="deliveries-refresh">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </CardContent>
      </Card>

      {!data ? (
        <div className="flex items-center gap-2 text-[#465B70]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : data.total === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-[#465B70] bg-white rounded-2xl border border-[#0A4D8C1A]">
          <CalendarIcon className="h-8 w-8 mb-2" />
          No deliveries in this range.
        </div>
      ) : (
        <div className="space-y-3" data-testid="deliveries-by-day">
          {days.map(({ day, deliveries }) => (
            <Card key={day} data-testid={`deliveries-day-${day}`}>
              <CardContent className="p-4">
                <p className="mb-3 text-xs uppercase tracking-[.18em] font-bold text-[#F25C05]">{new Date(day).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                <ul className="divide-y divide-[#0A4D8C14]">
                  {deliveries.map((d) => {
                    const ds = d.display_status || d.status;
                    const badge = STATUS_BADGES[ds] || STATUS_BADGES.scheduled;
                    return (
                    <li key={d.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`delivery-row-${d.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[#05223D]">{d.product_name} <span className="font-normal text-xs text-[#7A8A99]">× {d.qty}</span></div>
                        <div className="text-xs text-[#465B70] mt-0.5 flex items-center gap-1"><User size={11} /> {d.customer_name || '—'} {d.customer_phone && <span className="text-[#0A4D8C]">· {d.customer_phone}</span>} · {d.subscription_label}</div>
                      </div>
                      <Badge className={`rounded-full text-[10px] ${badge.cls}`} data-testid={`status-${d.id}`}>{badge.label}</Badge>
                      {d.status !== 'delivered' && d.status !== 'cancelled' && (
                        <Button size="sm" onClick={() => onMarkDelivered(d.id)} disabled={!!busy[d.id]} className="h-8 rounded-full bg-[#F25C05] hover:bg-[#D95204] text-white text-xs" data-testid={`mark-delivered-${d.id}`}>
                          {busy[d.id] ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Truck className="mr-1.5 h-3 w-3" />}
                          Mark delivered
                        </Button>
                      )}
                      {d.payment_status === 'paid' && (
                        <Badge className="rounded-full text-[10px] bg-emerald-100 text-emerald-800">💰 Paid</Badge>
                      )}
                    </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
